import type { Task, TaskPriority } from "@/planning/model/wire.js";
import { DateField, EntityFormModal, SelectField, TextArea, TextField } from "@/platform/ui";
import { combineDateTime, splitDateTime } from "@/platform/client";
import { useEntityForm } from "@/platform/client";
import { useCreateTask, useDeleteTask, useUpdateTask } from "@/planning/ui/queries/tasks.js";
import { useTz } from "@/platform/client";

interface FormState {
  title: string;
  notes: string;
  due: string; // YYYY-MM-DD or ""
  priority: TaskPriority;
}

export function TaskModal({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing: Task | null;
}) {
  const create = useCreateTask();
  const update = useUpdateTask();
  const del = useDeleteTask();
  const tz = useTz();

  const form = useEntityForm<Task, FormState>({
    open,
    editing,
    onClose,
    initial: (t) => ({
      title: t?.title ?? "",
      notes: t?.notes ?? "",
      due: t?.dueAt ? splitDateTime(t.dueAt, tz).date : "",
      priority: t?.priority ?? "normal",
    }),
    save: (f, t) => {
      const dueAt = f.due ? combineDateTime(f.due, "09:00", tz) : null;
      if (t) {
        return update.mutateAsync({
          id: t.id,
          patch: {
            title: f.title.trim(),
            notes: f.notes.trim() || null,
            dueAt,
            priority: f.priority,
          },
        });
      }
      return create.mutateAsync({
        title: f.title.trim(),
        notes: f.notes.trim() || undefined,
        dueAt: dueAt ?? undefined,
        priority: f.priority,
        tags: [],
      });
    },
    remove: (t) => del.mutateAsync(t.id),
  });

  const { form: f, patch, submit } = form;

  return (
    <EntityFormModal
      open={open}
      form={form}
      noun="task"
      describe={(t) => <span className="font-bold text-ink">“{t.title}”</span>}
      canSubmit={f.title.trim() !== ""}
    >
      <TextField
        label="Task"
        autoFocus
        value={f.title}
        onChange={(title) => patch({ title })}
        onEnter={submit}
        placeholder="What needs doing?"
      />
      <TextArea
        label="Description"
        value={f.notes}
        onChange={(notes) => patch({ notes })}
        onEnter={submit}
        placeholder="Add details (optional)"
      />
      <div className="grid grid-cols-2 gap-3">
        <DateField label="Due date" value={f.due} onChange={(due) => patch({ due })} />
        <SelectField
          label="Priority"
          value={f.priority}
          onChange={(priority) => patch({ priority })}
          options={[
            { value: "low", label: "Low" },
            { value: "normal", label: "Normal" },
            { value: "high", label: "High" },
          ]}
        />
      </div>
    </EntityFormModal>
  );
}
